import { PureComponent, memo, createRef } from 'react';
import classNames from 'classnames/bind';
import { InView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';

import withComponentName from '../../../../../decorators/withComponentName';

import ButtonComponent from '../../../../Buttons/ButtonComponent';
import VideoSlide from './VideoSlide';
import Slider from '../../../../Shared/ReactSlick';

import Styles from '../styles.module.scss';

import { sliderData } from '../../../../../data/homepage/getStartedData';

const cx = classNames.bind(Styles);

const NextArrow = memo((props) => {
  const { onClick } = props;
  return (
    <div
      className={Styles['button-next']}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faChevronDown} />
    </div>
  );
});

const PrevArrow = memo((props) => {
  const { onClick } = props;
  return (
    <div
      className={Styles['button-prev']}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faChevronUp} />
    </div>
  );
});

class DriveDueDiligenceSection extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      nav1: null,
      nav2: null,
      activeTab: 'desktop',
      inView: false,
    };
    this.onObserverChange = this.onObserverChange.bind(this);
    this.sliderWrapper = createRef();

    this.settings = {
      fade: true,
      slidesToShow: 1,
      centerMode: true,
      infinite: false,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      centerPadding: '35px',
      beforeChange: () => {
        this.setState({ activeTab: '' });
        setTimeout(() => {
          this.setState({ activeTab: 'desktop' });
        }, 10);
      },
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            dots: false,
            fade: false,
            slidesToShow: 1,
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
            centerPadding: '35px',
            beforeChange: () => {
              this.setState({ activeTab: '' });
              setTimeout(() => {
                this.setState({ activeTab: 'desktop' });
              }, 100);
            },
          },
        },
      ],
    };
    this.navSettings = {
      slidesToShow: 9,
      swipeToSlide: true,
      focusOnSelect: true,
      centerMode: false,
      vertical: true,
      infinite: false,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 1,
            vertical: false,
            dots: true,
            centerMode: true,
            infinite: true,
          },
        },
      ],
    };
  }

  componentDidMount() {
    this.setState({
      nav1: this.slider1,
      nav2: this.slider2,
    });
  }

  changeTab(event, tabName) {
    event.preventDefault();
    this.setState({
      activeTab: tabName,
    });
  }

  onObserverChange(inView) {
    this.setState({ inView });
  }

  render() {
    const {
      nav2,
      nav1,
      activeTab,
      inView,
    } = this.state;

    return (
      <InView
        as="section"
        className={cx('due-diligence-section', 'drive-due-diligence')}
        onChange={this.onObserverChange}
        skip={inView}
        rootMargin="150px"
        ref={(ref) => {
          if (ref?.node) {
            this.sliderWrapper.current = ref.node;
          }
        }}
      >
        <div className={Styles['due-diligence-title']}>
          <h1 className={`${Styles.title} text-uppercase`}>
            A Wide Range of Data in One Platform
          </h1>
        </div>
        <div className={cx('sliders-holder')}>
          <Slider
            className={Styles['due-diligence-slider']}
            asNavFor={nav1}
            ref={(slider) => { this.slider2 = slider; }}
            {...this.navSettings}
          >
            <div className={cx('main-slide')}>
              <h6>Get Started Within A Few Minutes</h6>
              <p>
                Track relevant content for stocks, private companies,&nbsp;
                sectors, locations, and 300k topics in one place.
              </p>
              <p>
                Over 1 billion data points covering news, company filings,&nbsp;
                Twitter, and fundamental and alternative data in 50+ languages.
              </p>
              <ButtonComponent
                withoutReload
                value="Get Started For Free"
                path="/news/onboarding/watchlist"
              />
            </div>
            <div className={cx('main-slide')}>
              <h6>Full Coverage of Asset Classes</h6>
              <p>
                Start with a broad overview of an asset class before targeting&nbsp;
                your research to specific investment vehicles.
              </p>
              <p>
                Over 1 billion data points covering news, company filings,&nbsp;
                Twitter, and fundamental and alternative data in 50+ languages.
              </p>
              <ButtonComponent
                withoutReload
                value="See other asset classes"
                path="/news/directory"
              />
            </div>
            <div className={cx('main-slide')}>
              <h6>An API to Automate and Build Experiences</h6>
              <p>
                Automate internal processes, ingest data for investing and&nbsp;
                trading, or whitelabel our data to inform and engage your end users
              </p>
              <ButtonComponent
                withoutReload
                value="Learn More"
                short
                path="/products/api/financial-news/pricing-api/commercial"
              />
            </div>
            <div className={cx('main-slide')}>
              <h6>Access Paywall-free Content From 1000+ Publications</h6>
              <p>Gold plan users enjoy select content without paywalls or advertising. </p>
              <ButtonComponent
                value="See List of Publications"
                path="https://help.cityfalcon.com/hc/en-us/articles/360034657073"
                target="_blank"
                rel="noopener noreferrer"
              />
            </div>
            <div className={cx('main-slide')}>
              <h6>
                Summarise Content From
                {' '}
                <span>100s</span>
                {' '}
                Of Stories Into Just a Few Lines
              </h6>
              <p>
                AI-driven summarisation condenses all the data out there&nbsp;
                to streamline your news consumption and due diligence research.
              </p>
              <p>Make better decisions, faster.</p>
              <ButtonComponent
                value="Learn More"
                path="https://help.cityfalcon.com/hc/en-us/articles/360006073397"
                target="_blank"
                rel="noopener noreferrer"
              />
            </div>
            <div className={cx('main-slide')}>
              <h6>Understand Price Movements For Securities - Stocks, Cryptos And More</h6>
              <p>
                Are you struggling to figure out why the price&nbsp;
                of a stock or crypto jumped or fell?
              </p>
              <p>
                Identify top stories for every price point and&nbsp;
                movement to clarify why your investments moved.
              </p>
            </div>
            <div className={cx('main-slide')}>
              <h6>Feel the Markets with Sentiment Analysis</h6>
              <p>
                Use the positivity or negativity of the news and social&nbsp;
                media as one more dimension in your due diligence and investment decisions.
              </p>
              <ButtonComponent
                value="Learn More"
                path="https://help.cityfalcon.com/hc/en-us/articles/360006381518"
                target="_blank"
                rel="noopener noreferrer"
              />
            </div>
            <div className={cx('main-slide')}>
              <h6>Follow the Trend, Understand Market Drivers</h6>
              <p>
                Powerful algorithms comb Big Data in real time to identify&nbsp;
                and highlight trends. Focus the search by topic class, event, and timeframe.
              </p>
              <ButtonComponent
                value="Learn More"
                path="https://help.cityfalcon.com/hc/en-us/articles/360002510494"
                target="_blank"
                rel="noopener noreferrer"
              />
            </div>
            <div className={cx('main-slide')}>
              <h6>Customise And Keep Your Feed Clean, Unique</h6>
              <p>
                Multilingual similar content grouped together&nbsp;
                for easier due diligence and research.
              </p>
              <p>
                Customisable layouts serve the different needs&nbsp;
                of investors, traders and others.
              </p>
            </div>
          </Slider>
          <Slider
            className={Styles['due-diligence-navigation-slider']}
            {...this.settings}
            asNavFor={nav2}
            ref={(slider) => { this.slider1 = slider; }}
          >
            {sliderData.map((slide) => (
              <div className={Styles.slide} key={slide.id}>
                <div className={cx('tabs', { hidden: slide.tabs.length === 1 })}>
                  {slide.tabs.map((tab, i) => (
                    <div
                      className={cx('tab', { active: activeTab === tab })}
                      onClick={(e) => this.changeTab(e, tab)}
                      key={tab}
                    >
                      {(i === 0 && slide.id === 2) ? 'Stocks' : null}
                      {(i === 1 && slide.id === 2) ? 'Cryptos' : null}
                      {slide.id !== 2 ? tab : null}
                    </div>
                  ))}
                  {slide.id === 2 && (
                    <div
                      className={cx('tab', 'disabled')}
                    >
                      More videos soon
                    </div>
                  )}
                </div>
                {slide.videos.map((video) => (
                  <div key={video.id} hidden={activeTab !== video.activeTab}>
                    {inView && (
                      <VideoSlide
                        url={video.url}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </Slider>
        </div>
      </InView>
    );
  }
}

export default withComponentName(DriveDueDiligenceSection);
